import React, { createContext, useContext, useState, ReactNode } from 'react';
import { NavigationState, DemoSettings } from '../../types/navigation';

interface NavigationContextType {
  state: NavigationState;
  updateState: (updates: Partial<NavigationState>) => void;
  updateDemoSettings: (settings: Partial<DemoSettings>) => void;
  toggleMobileMenu: () => void;
  closeMobileMenu: () => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

interface NavigationProviderProps {
  children: ReactNode;
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children }) => {
  const [state, setState] = useState<NavigationState>({
    section: 'home',
    product: null,
    page: null,
    path: '/',
    mobileMenuOpen: false,
    demoSettings: {
      productsAboveNav: false,
      showProductLogos: false,
      hoverEffects: true,
      activeStates: true
    }
  });

  const updateState = (updates: Partial<NavigationState>) => {
    setState(prev => ({ ...prev, ...updates }));
  };

  const updateDemoSettings = (settings: Partial<DemoSettings>) => {
    setState(prev => ({ 
      ...prev, 
      demoSettings: { ...prev.demoSettings, ...settings }
    }));
  };

  const toggleMobileMenu = () => {
    setState(prev => ({ ...prev, mobileMenuOpen: !prev.mobileMenuOpen }));
  };

  const closeMobileMenu = () => {
    setState(prev => ({ ...prev, mobileMenuOpen: false }));
  };

  return (
    <NavigationContext.Provider value={{ state, updateState, updateDemoSettings, toggleMobileMenu, closeMobileMenu }}>
      {children}
    </NavigationContext.Provider>
  );
};